// Discogs / Gemini / Spotify — 100% CLIENT-SIDE implementation.
// Works with or without a backend. When REACT_APP_BACKEND_URL is empty (e.g. on
// a Netlify frontend-only deploy), all requests are made directly from the
// browser using CORS-enabled public APIs. API keys are stored in Firestore
// (settings/api_keys) and read on demand — with hardcoded fallbacks so the
// app works out-of-the-box on a fresh Netlify deployment.
import { getById } from "./firestore";

// ------- DEFAULT API KEYS (fallbacks when Firestore is empty) -------
// These are used only if no Firestore override exists. Admin can still change
// them via Barkod → API Açarları and their choice will take precedence.
const DEFAULT_DISCOGS_TOKEN = "IlkualOjpOqBxMyZtIcgQNZeBPfCiwMHuJbCOveP";
const DEFAULT_GEMINI_KEY = "AQ.Ab8RN6KSIYO5rf-P5GlgDLh1TUlSsL42gCr84MeTkJIwrJVC-Q";

const BACKEND_URL =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
// Detect legit backend URLs — any http(s) endpoint. Empty string → client-only.
const HAS_BACKEND = /^https?:\/\//.test(BACKEND_URL);

function apiUrl(path) {
  const base = BACKEND_URL.replace(/\/$/, "");
  return `${base}/api${path}`;
}

// Discogs image URLs from i.discogs.com are safe to render in <img> tags,
// browsers allow cross-origin images. No proxying needed.
export function resolveBackendUrl(u) {
  if (!u) return u;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api")) {
    if (HAS_BACKEND) {
      const base = BACKEND_URL.replace(/\/$/, "");
      return `${base}${u}`;
    }
    // No backend — try to extract the original URL from proxy query
    try {
      const m = u.match(/[?&]url=([^&]+)/);
      if (m) return decodeURIComponent(m[1]);
    } catch { /* ignore */ }
  }
  return u;
}

export async function getStoredApiKeys() {
  let stored = {};
  try {
    stored = (await getById("settings", "api_keys")) || {};
  } catch { /* ignore */ }
  // Merge Firestore values over defaults (Firestore wins if present)
  return {
    discogs_token: (stored.discogs_token && String(stored.discogs_token).trim()) || DEFAULT_DISCOGS_TOKEN,
    gemini_api_key: (stored.gemini_api_key && String(stored.gemini_api_key).trim()) || DEFAULT_GEMINI_KEY,
    spotify_client_id: stored.spotify_client_id || "",
    spotify_client_secret: stored.spotify_client_secret || "",
    ...stored,
  };
}

async function tokenOr(passed) {
  if (passed) return passed;
  const keys = await getStoredApiKeys();
  return keys.discogs_token || DEFAULT_DISCOGS_TOKEN;
}

const DISCOGS_UA = "VinylBakuAdmin/1.0";

// -------------------- DISCOGS --------------------
export async function searchDiscogsByBarcode(barcode, opts = {}) {
  const token = await tokenOr(opts.token);
  if (!token && !HAS_BACKEND) throw new Error("Discogs token yoxdur. Vault → API Açarları bölməsində əlavə edin.");

  if (HAS_BACKEND) {
    // Legacy path — call backend proxy
    const r = await fetch(apiUrl("/discogs/search-by-barcode"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ barcode, token }),
    });
    if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
    const data = await r.json();
    (data.results || []).forEach((it) => {
      it.thumb = resolveBackendUrl(it.thumb);
      it.cover_image = resolveBackendUrl(it.cover_image);
    });
    return data;
  }

  // CLIENT-SIDE: direct call to Discogs (CORS-enabled)
  const url = `https://api.discogs.com/database/search?barcode=${encodeURIComponent(barcode)}&type=release&per_page=25`;
  const r = await fetch(url, {
    headers: {
      "Authorization": `Discogs token=${token}`,
      "User-Agent": DISCOGS_UA,
      "Accept": "application/json",
    },
  });
  if (!r.ok) throw new Error(`Discogs ${r.status}: ${await r.text().catch(() => "")}`);
  const data = await r.json();
  return {
    results: (data.results || []).map((r) => ({
      id: r.id,
      type: r.type,
      title: r.title,
      year: r.year,
      country: r.country,
      format: r.format,
      label: r.label,
      genre: r.genre,
      style: r.style,
      thumb: r.thumb,
      cover_image: r.cover_image,
      resource_url: r.resource_url,
      master_id: r.master_id,
      master_url: r.master_url,
    })),
  };
}

export async function getDiscogsRelease(releaseId, opts = {}) {
  const token = await tokenOr(opts.token);

  if (HAS_BACKEND) {
    const r = await fetch(apiUrl("/discogs/release"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ release_id: Number(releaseId), token }),
    });
    if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
    const data = await r.json();
    (data.images || []).forEach((im) => {
      im.uri = resolveBackendUrl(im.uri);
      im.uri150 = resolveBackendUrl(im.uri150);
    });
    return data;
  }

  // CLIENT-SIDE
  const headers = { "User-Agent": DISCOGS_UA, "Accept": "application/json" };
  if (token) headers["Authorization"] = `Discogs token=${token}`;
  const r = await fetch(`https://api.discogs.com/releases/${releaseId}`, { headers });
  if (!r.ok) throw new Error(`Discogs release ${r.status}`);
  const rel = await r.json();

  // Extract artist name (first credit)
  const artist = (rel.artists || [])
    .map((a) => (a.name || "").replace(/\s*\(\d+\)$/, "").trim())
    .filter(Boolean)
    .join(", ");

  // Extract genres/styles as tags
  const tags = [...(rel.genres || []), ...(rel.styles || [])];

  // Format / RPM / weight from formats[]
  const fmt = (rel.formats || [])[0] || {};
  const formatName = fmt.name || "";
  const descriptions = fmt.descriptions || [];
  const rpm = descriptions.find((d) => /^\d+\s*RPM$/i.test(d)) || descriptions.find((d) => /RPM/i.test(d)) || "";
  const weight = descriptions.find((d) => /\d+\s*g\b/i.test(d)) || "";

  // Tracklist
  const tracklist = (rel.tracklist || []).map((t) => ({
    position: t.position,
    title: t.title,
    duration: t.duration,
  }));

  return {
    id: rel.id,
    artist,
    title: rel.title,
    year: rel.year,
    country: rel.country,
    label: (rel.labels || []).map((l) => l.name).join(", "),
    catno: (rel.labels || [])[0]?.catno || "",
    genres: rel.genres || [],
    styles: rel.styles || [],
    format: formatName,
    format_descriptions: descriptions,
    rpm,
    weight,
    tags,
    images: rel.images || [],
    tracklist,
    notes: rel.notes || "",
    thumb: rel.thumb,
    cover_image: (rel.images || [])[0]?.uri || rel.thumb || "",
    barcode: (rel.identifiers || []).find((i) => i.type === "Barcode")?.value || "",
  };
}

export async function spotifySearchAlbum(artist, album) {
  // Simple fallback — return a Spotify search URL admin can open manually.
  const q = `${artist || ""} ${album || ""}`.trim();
  return {
    albums: [],
    search_url: q ? `https://open.spotify.com/search/${encodeURIComponent(q)}` : "",
    query: q,
  };
}

// -------------------- SPOTIFY URL PARSING / VERIFICATION --------------------
// Extract a 22-char Spotify album ID from any URL/URI/text form.
export function extractSpotifyAlbumId(text) {
  if (!text) return "";
  const s = String(text).trim();
  if (!s) return "";
  const patterns = [
    /spotify:album:([A-Za-z0-9]{22})/,
    /[/]album[/]([A-Za-z0-9]{22})/,
    /^([A-Za-z0-9]{22})$/,
  ];
  for (const p of patterns) {
    const m = s.match(p);
    if (m) return m[1];
  }
  return "";
}

// Verify a Spotify album ID is real via public oEmbed (CORS-enabled).
export async function verifySpotifyAlbum(albumId) {
  if (!albumId || albumId.length !== 22) return false;
  try {
    const r = await fetch(
      `https://open.spotify.com/oembed?url=https://open.spotify.com/album/${albumId}`,
      { method: "GET" }
    );
    return r.ok;
  } catch {
    return false;
  }
}

// Normalise any Spotify URL/URI/share link into a canonical album URL + ID.
export async function resolveSpotifyUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return { album_id: "", canonical_url: "", share_url: "", verified: false };

  // Skip search URLs — those aren't album links
  if (/open\.spotify\.com\/search\//i.test(raw)) {
    return { album_id: "", canonical_url: "", share_url: "", verified: false, error: "search_url" };
  }

  let albumId = extractSpotifyAlbumId(raw);

  // Short link (spotify.link/xxx) — CORS-blocked in browser. Fall back to backend if available.
  if (!albumId && /^https?:\/\/(spotify\.link|spoti\.fi)\//i.test(raw)) {
    if (HAS_BACKEND) {
      const r = await fetch(apiUrl("/spotify/resolve-url"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: raw }),
      });
      if (r.ok) return r.json();
    }
    return { album_id: "", canonical_url: "", share_url: "", verified: false, error: "short_link_needs_backend" };
  }

  if (!albumId) return { album_id: "", canonical_url: "", share_url: "", verified: false };

  const canonical = `https://open.spotify.com/album/${albumId}`;
  const verified = await verifySpotifyAlbum(albumId);
  return { album_id: albumId, canonical_url: canonical, share_url: canonical, verified };
}

// -------------------- AI ENRICHMENT (Gemini, client-side) --------------------
// Calls Gemini directly with the key stored in Firestore. Falls back to backend
// if backend is available (dev environment).
export async function aiEnrichProduct(payload) {
  const keys = await getStoredApiKeys();

  if (HAS_BACKEND) {
    // Prefer backend when available — it has extra strategies (Spotify API, grounding, MusicBrainz)
    const body = {
      ...payload,
      gemini_api_key: keys.gemini_api_key || "",
      spotify_client_id: keys.spotify_client_id || "",
      spotify_client_secret: keys.spotify_client_secret || "",
    };
    const r = await fetch(apiUrl("/ai/enrich-product"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
    return r.json();
  }

  // CLIENT-SIDE: Direct Gemini call for descriptions.
  const geminiKey = (keys.gemini_api_key || DEFAULT_GEMINI_KEY || "").trim();
  if (!geminiKey) {
    throw new Error("Gemini API açarı yoxdur. Vault → API Açarları bölməsində əlavə edin.");
  }

  const artist = payload.artist || "";
  const album = payload.album || "";
  const year = payload.year || "";
  const barcode = payload.barcode || "";
  const genres = (payload.genres || []).join(", ");
  const styles = (payload.styles || []).join(", ");

  const prompt = `You are a vinyl record cataloguing assistant for VinylBaku (Azerbaijani store).
Given the release below, respond ONLY with a JSON object of shape:
{
  "spotify_album_url": "https://open.spotify.com/album/... (or empty string if unsure)",
  "description_az": "2-4 sentence engaging description in Azerbaijani",
  "description_en": "2-4 sentence engaging description in English",
  "description_ru": "2-4 sentence engaging description in Russian"
}

Release:
- Artist: ${artist}
- Album: ${album}
- Year: ${year}
- Barcode/UPC: ${barcode}
- Genres: ${genres}
- Styles: ${styles}

Rules:
1. For spotify_album_url: only include if you are confident about the exact 22-char album ID. Otherwise return empty string.
2. Descriptions should highlight the music, artistic significance, and appeal for vinyl collectors.
3. Return ONLY valid JSON, no markdown fences, no explanation.`;

  // Try multiple models in case of quota/availability issues
  const models = ["gemini-flash-latest", "gemini-flash-lite-latest", "gemini-2.5-flash", "gemini-pro-latest"];
  let body = null;
  let lastErr = "";
  for (const model of models) {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(geminiKey)}`;
    try {
      const gr = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: { temperature: 0.4, responseMimeType: "application/json" },
        }),
      });
      if (gr.ok) {
        body = await gr.json();
        break;
      }
      const t = await gr.text().catch(() => "");
      lastErr = `Gemini ${model} ${gr.status}: ${t.slice(0, 200)}`;
      // 429 / 404 / 503 → try next model
      if (![404, 429, 503].includes(gr.status)) {
        // Any other error is likely fatal (auth, bad request), still try next just in case
      }
    } catch (e) {
      lastErr = `Gemini ${model} network: ${e.message || e}`;
    }
  }
  if (!body) {
    throw new Error(lastErr || "Gemini bütün modellərdə uğursuz oldu.");
  }
  const parts = ((body.candidates || [{}])[0].content || {}).parts || [];
  const text = parts.map((p) => p.text || "").join("").trim();
  let parsed = {};
  try {
    parsed = JSON.parse(text);
  } catch {
    // Try to extract JSON from text
    const m = text.match(/\{[\s\S]*\}/);
    if (m) {
      try { parsed = JSON.parse(m[0]); } catch { parsed = {}; }
    }
  }

  // Verify Spotify URL if provided
  const aiUrl = (parsed.spotify_album_url || "").trim();
  const aiAlbumId = extractSpotifyAlbumId(aiUrl);
  let resolvedUrl = "";
  let strategy = "none";
  if (aiAlbumId) {
    const ok = await verifySpotifyAlbum(aiAlbumId);
    if (ok) {
      resolvedUrl = `https://open.spotify.com/album/${aiAlbumId}`;
      strategy = "ai_verified";
    }
  }

  // Fallback: search URL (admin needs to paste manually)
  const fallbackSearch = `https://open.spotify.com/search/${encodeURIComponent(`${artist} ${album}`.trim())}`;

  return {
    spotify_album_url: resolvedUrl || fallbackSearch,
    spotify_verified: !!resolvedUrl,
    spotify_strategy: resolvedUrl ? strategy : "fallback_search",
    description_az: parsed.description_az || "",
    description_en: parsed.description_en || "",
    description_ru: parsed.description_ru || "",
  };
}

// -------------------- SPOTIFY REAL SEARCH (client-side, no backend) --------------------
// Uses Client Credentials flow entirely in the browser. Both accounts.spotify.com
// and api.spotify.com have permissive CORS so this works from Netlify.
// ⚠️ NOTE: Spotify's new policy requires the app owner to have an active Premium
// subscription — without it, all endpoints return 403. In that case the UI
// shows a helpful error and admin should use the "Spotify-də tap" flow.
let _spotifyTokenCache = { token: "", expiresAt: 0 };

async function _spotifyGetClientToken(clientId, clientSecret) {
  if (!clientId || !clientSecret) throw new Error("Spotify Client ID/Secret yoxdur");
  const now = Date.now();
  if (_spotifyTokenCache.token && _spotifyTokenCache.expiresAt > now + 30_000) {
    return _spotifyTokenCache.token;
  }
  const body = new URLSearchParams({
    grant_type: "client_credentials",
    client_id: clientId,
    client_secret: clientSecret,
  });
  const r = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!r.ok) {
    const t = await r.text().catch(() => "");
    throw new Error(`Spotify auth ${r.status}: ${t.slice(0, 200)}`);
  }
  const j = await r.json();
  _spotifyTokenCache = {
    token: j.access_token,
    expiresAt: now + ((j.expires_in || 3600) * 1000),
  };
  return _spotifyTokenCache.token;
}

export async function spotifySearchReal({ artist = "", album = "", barcode = "", limit = 8 }) {
  const keys = await getStoredApiKeys();
  const clientId = (keys.spotify_client_id || "").trim();
  const clientSecret = (keys.spotify_client_secret || "").trim();

  if (!clientId || !clientSecret) {
    // Backwards-compat: if backend exists, use it
    if (HAS_BACKEND) {
      const r = await fetch(apiUrl("/spotify/search-real"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ artist, album, barcode, limit, client_id: clientId, client_secret: clientSecret }),
      });
      if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
      return r.json();
    }
    throw new Error("Spotify Client ID/Secret Vault → API Açarları bölməsində əlavə edilməlidir.");
  }

  const token = await _spotifyGetClientToken(clientId, clientSecret);

  const queries = [];
  if (barcode) queries.push(`upc:${barcode}`);
  if (artist && album) queries.push(`album:"${album}" artist:"${artist}"`);
  else if (album) queries.push(`album:"${album}"`);
  else if (artist) queries.push(`artist:"${artist}"`);

  const albums = [];
  const seen = new Set();

  for (const q of queries) {
    if (albums.length >= limit) break;
    const url = `https://api.spotify.com/v1/search?q=${encodeURIComponent(q)}&type=album&limit=${limit}`;
    const r = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
    if (!r.ok) {
      const errText = await r.text().catch(() => "");
      if (r.status === 403) {
        throw new Error(
          "Spotify Web API bloklanmış: 'Active Premium subscription required for the owner of the app'. " +
          "Developer hesabınızda Spotify Premium aktiv olmalıdır. Alternativ: 'Spotify-də tap' düyməsi ilə əl ilə linki götürün."
        );
      }
      if (r.status === 401) {
        // Token expired — retry once with fresh token
        _spotifyTokenCache = { token: "", expiresAt: 0 };
        throw new Error(`Spotify 401 unauthorized. Retry.`);
      }
      throw new Error(`Spotify search ${r.status}: ${errText.slice(0, 200)}`);
    }
    const data = await r.json();
    const items = (data.albums && data.albums.items) || [];
    for (const it of items) {
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      albums.push({
        id: it.id,
        url: it.external_urls?.spotify || `https://open.spotify.com/album/${it.id}`,
        name: it.name,
        artist: (it.artists || []).map((a) => a.name).join(", "),
        release_date: it.release_date,
        total_tracks: it.total_tracks,
        image: (it.images || [])[0]?.url || "",
      });
    }
  }

  const searchUrl = `https://open.spotify.com/search/${encodeURIComponent(`${artist} ${album}`.trim())}`;
  return { albums: albums.slice(0, limit), search_url: searchUrl, query: `${artist} ${album}`.trim() };
}
